import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Select,
  Tooltip,
} from 'antd'
import { Col } from 'antd'
import { CloseCircleOutlined } from '@ant-design/icons'
import * as React from 'react'
import { GatewayCommission } from '../../types'
import { removeLocalCommission, setDeleteCommission } from '../../model'
import { CommissionType } from './component'

type Props = {
  element: GatewayCommission
  index: number
  loading: boolean
  handleCommissionChange: (index: number, objectKey: string, value: any) => void
}

const operationTypeMap: { [key: string]: string } = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  dividend_withdrawal: 'Dividend withdrawal',
}

export const CommissionFormItem: React.FC<Props> = ({
  element,
  index,
  loading,
  handleCommissionChange,
}) => {
  return (
    <Col span={12}>
      <Card
        title={
          operationTypeMap[Number(element.operationType)] ||
          element.operationType
        }
        extra={
          <Button
            onClick={() =>
              element.isLocal
                ? removeLocalCommission(index)
                : setDeleteCommission(element)
            }
            disabled={loading}
            danger
            icon={<CloseCircleOutlined />}
          />
        }
      >
        <Form.Item label="Operation type" required>
          <Select
            value={element.operationType || undefined}
            placeholder="Select type"
            onChange={(value) =>
              handleCommissionChange(index, 'operationType', value)
            }
          >
            <Select.Option value="deposit">Deposit</Select.Option>
            <Select.Option value="withdrawal">Withdrawal</Select.Option>
            <Select.Option value="dividend_withdrawal">
              Dividend withdrawal
            </Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <Form.Item label="Acquirer" required>
          <Select
            value={element.acquirer || undefined}
            onChange={(value) =>
              handleCommissionChange(index, 'acquirer', value)
            }
          >
            <Select.Option value="inner">Inner</Select.Option>
            <Select.Option value="vendor">Vendor</Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <Form.Item label="Commission type" required>
          <Select
            value={element.commissionType || undefined}
            placeholder="Select type"
            onChange={(value) =>
              handleCommissionChange(index, 'commissionType', value)
            }
          >
            <Select.Option value={CommissionType.Calculated}>
              Calculated
            </Select.Option>
            <Select.Option value={CommissionType.Vendor}>Vendor</Select.Option>
            <Select.Option value={CommissionType.Custom}>Custom</Select.Option>
            <Select.Option value={CommissionType.KoraVat}>
              Kora VAT
            </Select.Option>
            <Select.Option
              value={CommissionType.KoraLimit}
              disabled={element.acquirer !== 'vendor'}
            >
              <Tooltip
                title={
                  element.acquirer !== 'vendor'
                    ? 'Available only when acquirer is Vendor'
                    : undefined
                }
              >
                <span>Kora limit</span>
              </Tooltip>
            </Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <Form.Item label="Payer" required>
          <Select
            value={element.payer || undefined}
            placeholder="Select type"
            onChange={(value) => handleCommissionChange(index, 'payer', value)}
          >
            <Select.Option value="client">Client</Select.Option>
            <Select.Option value="company">Company</Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <Form.Item label="Rounding mode">
          <Select
            value={element.roundingMode || undefined}
            placeholder="Select type"
            onChange={(value) =>
              handleCommissionChange(index, 'roundingMode', value)
            }
          >
            <Select.Option value="up">up</Select.Option>
            <Select.Option value="down">down</Select.Option>
            <Select.Option value="math">math</Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <Form.Item label="Flat amount">
          <Input
            value={element.flatAmount}
            onChange={(event) =>
              handleCommissionChange(index, 'flatAmount', event.target.value)
            }
          />
        </Form.Item>
        <Form.Item label="Flat currency">
          <Input
            value={element.flatCurrency}
            onChange={(event) =>
              handleCommissionChange(index, 'flatCurrency', event.target.value)
            }
          />
        </Form.Item>
        <Divider />
        <Form.Item label="Percent amount">
          <Input
            value={element.percentAmount}
            onChange={(event) =>
              handleCommissionChange(index, 'percentAmount', event.target.value)
            }
          />
        </Form.Item>
        <Divider />
        {element.commissionType !== CommissionType.KoraLimit && (
          <>
            <Form.Item label="Min Commission currency">
              <Input
                value={element.minCurrency}
                onChange={(event) =>
                  handleCommissionChange(
                    index,
                    'minCurrency',
                    event.target.value,
                  )
                }
              />
            </Form.Item>
            <Form.Item label="Min Commission amount">
              <Input
                value={element.minAmount}
                onChange={(event) =>
                  handleCommissionChange(index, 'minAmount', event.target.value)
                }
              />
            </Form.Item>
            <Form.Item label="Max Commission currency">
              <Input
                value={element.maxCurrency}
                onChange={(event) =>
                  handleCommissionChange(
                    index,
                    'maxCurrency',
                    event.target.value,
                  )
                }
              />
            </Form.Item>
            <Form.Item label="Max Commission amount">
              <Input
                value={element.maxAmount}
                onChange={(event) =>
                  handleCommissionChange(index, 'maxAmount', event.target.value)
                }
              />
            </Form.Item>
          </>
        )}
        <Divider />
        {(element.commissionType === CommissionType.KoraLimit ||
          element.commissionType === CommissionType.KoraVat) && (
            <>
              <strong>Custom params</strong>
              {element.commissionType === CommissionType.KoraLimit && (
                <>
                  <Form.Item label="VAT" style={{ marginTop: 12 }}>
                    <Input
                      value={element.customParams?.koraLimitParams?.VAT}
                      onChange={(event) => {
                        handleCommissionChange(index, 'customParams', {
                          ...element.customParams,
                          koraLimitParams: {
                            ...element.customParams?.koraLimitParams,
                            VAT: event.target.value,
                          },
                        })
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Limit">
                    <Input
                      value={element.customParams?.koraLimitParams?.limit}
                      onChange={(event) => {
                        handleCommissionChange(index, 'customParams', {
                          ...element.customParams,
                          koraLimitParams: {
                            ...element.customParams?.koraLimitParams,
                            limit: event.target.value,
                          },
                        })
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Added commission">
                    <Input
                      value={
                        element.customParams?.koraLimitParams?.addedCommission
                      }
                      onChange={(event) => {
                        handleCommissionChange(index, 'customParams', {
                          ...element.customParams,
                          koraLimitParams: {
                            ...element.customParams?.koraLimitParams,
                            addedCommission: event.target.value,
                          },
                        })
                      }}
                    />
                  </Form.Item>
                </>
              )}
              {element.commissionType === CommissionType.KoraVat && (
                <Form.Item label="VAT" style={{ marginTop: 12 }}>
                  <Input
                    value={element.customParams?.koraVATParams?.VAT}
                    onChange={(event) =>
                      handleCommissionChange(index, 'customParams', {
                        koraVATParams: {
                          VAT: event.target.value,
                        },
                      })
                    }
                  />
                </Form.Item>
              )}
            </>
          )}
        <Divider />
        <Checkbox
          checked={element.isEstimated}
          onChange={(event) =>
            handleCommissionChange(index, 'isEstimated', event.target.checked)
          }
        >
          Is estimated
        </Checkbox>
      </Card>
    </Col>
  )
}
